import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '@/services/logger';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import { SubscriptionProvider } from './contexts/SubscriptionContext';
import CookieConsent from './components/ui/CookieConsent';
import HashRedirect from './HashRedirect';
import AppInitializer from './components/app/AppInitializer';

import LoadingFallback from './components/app/LoadingFallback';
import { routes } from './config/routes.tsx';
import { enableAuthMonitoring } from '@/utils/authRecovery';

import './App.css';
import './styles/global.css';

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry auth errors
        if (error instanceof Error && 
           (error.message.includes('unauthorized') || error.message.includes('forbidden'))) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

function App() {
  logger.info('App component rendering');
  
  // Enable auth monitoring for automatic loop detection and recovery
  useEffect(() => {
    const disableMonitoring = enableAuthMonitoring();
    
    // Cleanup monitoring on unmount
    return () => {
      disableMonitoring();
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <AppInitializer>
        <QueryClientProvider client={queryClient}>
          <SubscriptionProvider>
            
            <Router>
              {/* Use Suspense with a more consistent LoadingFallback */}
              <Suspense fallback={<LoadingFallback message="Loading application..." />}>
                <HashRedirect />
                <Routes>
                  {routes.map(({ path, element, isPublic, requireAdmin, requireSubscription }) => {
                    logger.debug(`Setting up route: ${path}`, { 
                      isPublic, 
                      requireAdmin, 
                      requireSubscription 
                    });
                    
                    return (
                      <Route
                        key={path}
                        path={path}
                        element={element}
                      />
                    );
                  })}
                </Routes>
              </Suspense>
            </Router>
            <CookieConsent />
          </SubscriptionProvider>
        </QueryClientProvider>
      </AppInitializer>
    </ErrorBoundary>
  );
}

export default App;
